import {MapLayer} from "@esWebAPI/es/map/layer/MapLayer";
import {Map, source} from "azure-maps-control";
import {ILayerView, LayerInfo} from "@esWebAPI/es/model/Layer";
import {DatasourceManager} from "@esWebAPI/es/map/module/DatasourceManager";
import {IEventChannel} from "@esWebAPI/es/model/Models";
import {AzurePolygonLayer} from "@esWebAPI/es/map/layer/azure/AzurePolygonLayer";
import {ThematicStylerFactory} from "@esWebAPI/es/map/styler/ThematicStylerFactory";
import {ColorThematicStyler} from "@esWebAPI/es/map/styler/ColorThematicStyler";
import TerritoryModel from "@esWebAPI/es/map/TerritoryModel";
import {ITerritoryBaseLayer, TerritoryBaseFeatureWorker} from "@esWebAPI/es/map/layer/territory/TerritoryLayer";
import * as geojson from "geojson";
import {TerritoryLayerUtils} from "@esWebAPI/es/map/layer/territory/TerritoryLayerUtils";
import {AzureLineLayer} from "@esWebAPI/es/map/layer/azure/AzureLineLayer";
import {NavUtils} from "@esWebAPI/es/util/NavUtils";


export class TerritoryPolygonRelatedLayer extends MapLayer implements ITerritoryBaseLayer {

    private readonly _lineLayer: AzureLineLayer;
    private readonly _polygonLayer: AzurePolygonLayer;
    private readonly colorStyler: ColorThematicStyler;
    private readonly _territoryModel: TerritoryModel;

    private datasource: source.Source|undefined;

    constructor(map: Map, layerView: ILayerView, layerInfo: LayerInfo,
                datasourceManager: DatasourceManager,
                stylerFactory: ThematicStylerFactory,
                territoryModel: TerritoryModel,
                eventChannel: IEventChannel) {
        super(map, layerView, layerInfo, datasourceManager, eventChannel);

        this.colorStyler = stylerFactory.getColorStyler(layerView);
        this._territoryModel = territoryModel;

        this._lineLayer = new AzureLineLayer(map, layerView, datasourceManager, this.colorStyler);
        this._polygonLayer = new AzurePolygonLayer(map, layerView, datasourceManager, this.colorStyler);

        this.createLayers().then(() => {
           this._onReady.resolve();
        });
    }

    private async createLayers() {
        this.datasource = await this.datasourceManager.create(this.layerView, this.layerInfo, false);
        await this._lineLayer.create(this.datasource);
        await this._polygonLayer.create(this.datasource);
    }

    public get territoryModel() {
        return this._territoryModel;
    }

    public get featureWorker(): TerritoryBaseFeatureWorker {
        const datasource: source.DataSource = this.datasource as source.DataSource;
        return {
            getAll(): Promise<geojson.Feature[]> {
                const fc = datasource.toJson();
                return Promise.resolve(fc.features as geojson.Feature[]);
            },
            getFeatureById(id: number): Promise<geojson.Feature> {
                const feature = datasource.getShapeById(id).toJson();
                return Promise.resolve(feature as geojson.Feature);

            }
        } as TerritoryBaseFeatureWorker;
    }

    public createLabel(feature: geojson.Feature): string {
        return TerritoryLayerUtils.createLabel(this.layerView, feature);
    }

    public getLayerIds(): string[] {
        return [this._polygonLayer.getLayerId(), this._lineLayer.getLayerId()];
    }

    public isRelatedExtraPointLayer() {
        return false;
    }

    public isOutlinesLayer() {
        return false;
    }

    public isVisibleRes(): boolean {
        //TODO Hack for states Upgrade task to upgrade min/max res?
        if (this.layerView.minResolution === 0 && this.layerView.maxResolution > 156543) {
            const minZoom = NavUtils.resolutionToZoom(this.layerView.maxResolution);
            const maxZoom = 5;
            const zoom = this.map.getCamera().zoom || 0;
            return maxZoom ? zoom < maxZoom && zoom >= minZoom : zoom >= minZoom;
        }
        return super.isVisibleRes();
    }

    public destroy(): void {
        super.destroy();
        this._lineLayer.remove();
        this._polygonLayer.remove();
        if (this.datasource) {
            this.datasourceManager.remove(this.datasource.getId());
        }
    }

    protected getColorThematicFilter() {
        return undefined;
    }

    protected getSymbolThematicFilter() {
        return undefined;
    }

    protected hideFeature() {}

    protected showFeatures() {}

    public async update() {
        await this._polygonLayer.update(this.layerView);
        await this._lineLayer.update(this.layerView);
    }

    public getActiveAzureLayerId(): string | undefined {
        return this._polygonLayer.getLayerId();
    }
}